import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import { useApiCall } from 'hooks/use-api-call';
import { AdminApi, AdminCampaign } from 'api';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import Section from 'ui/atoms/section';
import BoxedIssuerCampaign from 'apps/issuer/pages/product-details/campaigns/campaign-details/boxed-campaign';
import { currentTokenHasManageDistributionPermission } from 'src/apps/issuer/helpers/tokenPermissions';
import Link from 'core/routing/link';
import CampaignInvestments from './campaign-investments';
import Button from 'src/ui/atoms/button';
import AddInvestmentModal from './add-investment-modal';
import Header from 'src/ui/atoms/header';
import { hasDistributionCreateDataPermission } from 'src/apps/issuer/helpers/distributionPlatformPermissions';
import ActionButtons from 'src/ui/molecules/action-buttons';

const CampaignDetailsPage = (props: { campaignId: string }) => {
  const { campaignId } = props;

  const { withApi, makeAuthenticatedApi } = useApiCall(true);

  const [campaignDetails, setCampaignDetails] = useState<AdminCampaign>();
  const [isAddInvestmentModalOpen, setAddInvestmentModalOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const loadData = useCallback(() => {
    withApi(async () => {
      if (!campaignId) {
        return;
      }
      withApi(async () => {
        const campaignDetailsData = await api.adminCampaignsRetrieve({
          id: campaignId,
        });

        setCampaignDetails(campaignDetailsData);
      });
    });
  }, [withApi, api, campaignId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshTableData = useCallback(() => {
    setForceDataLoad(true);
    setAddInvestmentModalOpen(false);
  }, []);

  if (!campaignDetails) return null;

  const canManage = currentTokenHasManageDistributionPermission(campaignDetails.token);

  return (
    <WideContent
      title={
        campaignDetails.name && (
          <Translate name="issuerProductDetails.campaigns.campaignDetails.title" args={[campaignDetails.name]} />
        )
      }
      titleSpacing="larger"
    >
      <BoxedIssuerCampaign key={campaignDetails.id} issuersCanManage={canManage} {...campaignDetails} />
      <Section>
        <Header size="small" spacing="medium">
          <Translate name={'issuerProductDetails.campaigns.campaignDetails.investments.title'} />
        </Header>
        {hasDistributionCreateDataPermission([campaignDetails.distributionPlatformDetails]) &&
          (!campaignDetails.distributionPlatformDetails.usesEffecta || campaignDetails.distributionInformation) && (
            <Button
              variant="primary"
              icon="plus"
              iconColor="inherit"
              size="small"
              onClick={() => setAddInvestmentModalOpen(true)}
            >
              <Translate name="addInvestment.title" />
            </Button>
          )}
      </Section>
      <CampaignInvestments
        campaignId={campaignId}
        forceDataLoad={forceDataLoad}
        setForceDataLoad={setForceDataLoad}
        distributionPlatformDetails={campaignDetails.distributionPlatformDetails}
      />
      <Section>
        <ActionButtons align="start">
          <Link icon="arrow-left" to={`/dashboard/product/${campaignDetails.token.id}`}>
            <Translate as="span" name="issuerProductDetails.campaigns.campaignDetails.backToProductDetails" />
          </Link>
        </ActionButtons>
      </Section>

      {isAddInvestmentModalOpen && (
        <AddInvestmentModal
          onClose={() => setAddInvestmentModalOpen(false)}
          onAddNewInvestment={refreshTableData}
          campaignId={campaignId}
        />
      )}
    </WideContent>
  );
};

export default WithAuthenticatedPage(CampaignDetailsPage, AppType.ISSUER);
